import React, { useState } from 'react';
import { Card, Button, Row, Col, Badge, Spinner, Modal, Form, Alert, Toast, ToastContainer } from 'react-bootstrap';
import { Star, Download, Edit2, Save, AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { CpGpMatchedProduct, CoupangProduct } from '../../../../../models/Coupang';
import type { GlowpickProduct, ScAgentEngData } from '../../../../../models/Glowpick';
import axios from 'axios';
import { glowpickClient } from '../../../../../infrastructure/GlowpickClient';

// Ingredients Grid Component
interface Ingredient {
  korean: string;
  english: string;
  risk: string;
  score: number;
  desc: string;
}

const IngredientsGrid = ({ ingredients }: { ingredients: Ingredient[] }) => {
  const [showAll, setShowAll] = useState(false);
  const displayedIngredients = showAll ? ingredients : ingredients.slice(0, 8);

  return (
    <>
      <Row className="g-3">
        {displayedIngredients.map((ingredient, index) => (
          <Col key={index} md={3}>
            <div className="border rounded p-3 h-100 d-flex align-items-start">
              <span
                className={`badge me-2 mt-1 ${
                  ingredient.risk === 'low'
                    ? 'bg-success'
                    : ingredient.risk === 'mid'
                      ? 'bg-warning'
                      : ingredient.risk === 'high'
                        ? 'bg-danger'
                        : 'bg-secondary'
                }`}
                style={{ fontSize: '0.7rem', minWidth: '24px' }}
              >
                {ingredient.score}
              </span>
              <div className="flex-grow-1 min-w-0">
                <div className="fw-bold mb-1" style={{ fontSize: '0.9rem' }}>
                  {ingredient.korean}
                </div>
                <div className="text-muted small mb-2">{ingredient.english}</div>
                <div className="text-muted" style={{ fontSize: '0.75rem', lineHeight: '1.3' }}>
                  {ingredient.desc.length > 80 ? `${ingredient.desc.substring(0, 80)}...` : ingredient.desc}
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {ingredients.length > 8 && (
        <div className="text-center mt-3">
          <Button variant="outline-primary" size="sm" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show Less' : `Show More (${ingredients.length - 8} more ingredients)`}
          </Button>
        </div>
      )}
    </>
  );
};

interface ProductDetailsSectionProps {
  matchedProduct: CpGpMatchedProduct;
  glowpickProduct?: GlowpickProduct | undefined;
  coupangProduct?: CoupangProduct | undefined;
  unifiedProduct: any;
  cpProductId?: string | undefined;
  cpItemId?: string | undefined;
  onRefetch?: () => Promise<any>;
}

export const ProductDetailsSection: React.FC<ProductDetailsSectionProps> = ({
  matchedProduct,
  glowpickProduct,
  coupangProduct,
  unifiedProduct,
  cpProductId,
  cpItemId,
  onRefetch,
}) => {
  const queryClient = useQueryClient();
  const [showIngredients, setShowIngredients] = useState(false);

  // Translation state
  const [translationData, setTranslationData] = useState<ScAgentEngData | null>(null);
  const [isLoadingTranslation, setIsLoadingTranslation] = useState(false);
  const [isSavingTranslation, setIsSavingTranslation] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedData, setEditedData] = useState<ScAgentEngData | null>(null);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'success' | 'danger'>('success');

  const fetchTranslation = async () => {
    if (!matchedProduct.gpProductId) {
      alert('No Glowpick Product ID available');
      return;
    }

    setIsLoadingTranslation(true);
    try {
      const response = await axios.post(
        'https://n8n.skin-seoul.work/webhook/dc2a664d-67c7-454e-a82e-3ab2a0efc8d3',
        { gpi: matchedProduct.gpProductId },
        { headers: { 'Content-Type': 'application/json' } },
      );

      if (response.data) {
        setTranslationData(response.data);
        setToastMessage('Product validated & translated successfully!');
        setToastVariant('success');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Failed to fetch translation:', error);
      setToastMessage(`Failed to validate & translate: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsLoadingTranslation(false);
    }
  };

  const handleEditClick = () => {
    const dataToEdit = translationData || glowpickProduct?.scAgentEngData;
    if (dataToEdit) {
      setEditedData({ ...dataToEdit });
      setShowEditModal(true);
    }
  };

  const handleSaveToGlowpick = async () => {
    if (!editedData && !translationData) {
      alert('No translation data to save');
      return;
    }

    const dataToSave = editedData || translationData;

    setIsSavingTranslation(true);
    try {
      const result = await glowpickClient.updateProduct(matchedProduct.gpProductId, {
        scAgentEngData: dataToSave,
      });

      if (result.success) {
        setShowEditModal(false);
        setEditedData(null);
        setTranslationData(null); // Clear translation data to show fresh data from Glowpick

        // Invalidate React Query caches to force refetch of fresh data
        await queryClient.invalidateQueries({ queryKey: ['glowpickProduct', matchedProduct.gpProductId] });
        await queryClient.invalidateQueries({ queryKey: ['cpGpMatch', cpProductId, cpItemId] });

        // Refetch to get the updated product with fresh scAgentEngData
        if (onRefetch) {
          await onRefetch();
        }

        console.info('✅ Product info saved and caches invalidated - fresh data should be displayed');

        setToastMessage('Product info saved to Glowpick successfully!');
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage(`Failed to save to Glowpick: ${result.message || 'Unknown error'}`);
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Failed to save to Glowpick:', error);
      setToastMessage(`Failed to save to Glowpick: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsSavingTranslation(false);
    }
  };

  return (
    <>
      {/* Product Comparison - Original vs Translated */}
      <Card className="mb-4">
        <Card.Header>
          <h6 className="mb-0">Product Details</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Row className="g-0">
            {/* Left Column - Original Data */}
            <Col md={6} className="border-end">
              <div className="p-4">
                <div className="text-center mb-4">
                  <h6 className="mb-2 fw-bold" style={{ color: '#6c757d' }}>
                    Original Data
                  </h6>
                  <hr className="my-2" />
                </div>

                {/* Glowpick Data - Horizontal Layout */}
                <div className="mb-4 pb-3 border-bottom">
                  <div className="d-flex align-items-center mb-2">
                    <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#eb3d48' }}>GLOWPICK</span>
                  </div>
                  <div className="d-flex gap-3">
                    {/* Glowpick Thumbnail */}
                    <div className="flex-shrink-0">
                      <img
                        src={glowpickProduct?.image || '/placeholder-image.png'}
                        alt={glowpickProduct?.name || 'Glowpick Product'}
                        className="rounded border"
                        style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                      />
                    </div>
                    {/* Glowpick Text */}
                    <div className="flex-grow-1">
                      <div className="mb-2">
                        <small className="text-muted fw-bold">Brand</small>
                        <div style={{ fontSize: '0.85rem' }}>{glowpickProduct?.brand || 'N/A'}</div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Product Name</small>
                        <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                          {glowpickProduct?.name || 'Unknown Product'}
                        </div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Category</small>
                        <div style={{ fontSize: '0.85rem' }}>
                          {unifiedProduct.category}
                          {unifiedProduct.subcategory && ` > ${unifiedProduct.subcategory}`}
                        </div>
                      </div>

                      {unifiedProduct.glowpickRating && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Rating</small>
                          <div className="d-flex align-items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={12}
                                className={`${i < Math.floor(unifiedProduct.glowpickRating || 0) ? 'text-danger' : 'text-muted'}`}
                                fill="currentColor"
                              />
                            ))}
                            <span className="ms-2" style={{ fontSize: '0.85rem' }}>
                              {unifiedProduct.glowpickRating}
                            </span>
                            {unifiedProduct.glowpickReviews && (
                              <small className="text-muted ms-1">
                                ({unifiedProduct.glowpickReviews.toLocaleString()})
                              </small>
                            )}
                          </div>
                        </div>
                      )}

                      {unifiedProduct.awards.length > 0 && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Awards</small>
                          <div className="d-flex flex-wrap gap-1 mt-1">
                            {unifiedProduct.awards.slice(0, 2).map((award: any, index: number) => (
                              <span key={index} className="badge bg-warning text-dark" style={{ fontSize: '0.65rem' }}>
                                🏆 {award.title?.replace(' 글로우픽 어워드', '') || 'Award'}
                              </span>
                            ))}
                            {unifiedProduct.awards.length > 2 && (
                              <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                                +{unifiedProduct.awards.length - 2}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {glowpickProduct?.description && (
                    <div className="mt-2">
                      <small className="text-muted fw-bold">Description</small>
                      <div style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>{glowpickProduct.description}</div>
                    </div>
                  )}

                  {unifiedProduct.tags.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted fw-bold">Tags</small>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {unifiedProduct.tags.slice(0, 5).join(' • ')}
                      </div>
                    </div>
                  )}

                  {/* Ingredients Section */}
                  {unifiedProduct.ingredients.length > 0 && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div>
                          <small className="text-muted fw-bold">Ingredients</small>
                          <Badge bg="primary" className="ms-2" style={{ fontSize: '0.65rem' }}>
                            {unifiedProduct.ingredients.length} ingredients
                          </Badge>
                        </div>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowIngredients(!showIngredients)}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {showIngredients ? 'Hide' : 'Show'} Ingredients
                        </Button>
                      </div>

                      {showIngredients && (
                        <div className="mt-3">
                          {(() => {
                            const processIngredients = (ingredients: any[]) => {
                              return ingredients.map((ingredient) => {
                                const riskValue =
                                  typeof ingredient.risk === 'string'
                                    ? parseInt(ingredient.risk, 10)
                                    : ingredient.risk || 1;
                                let riskLevel = 'low';

                                if (riskValue >= 7) {
                                  riskLevel = 'high';
                                } else if (riskValue >= 3) {
                                  riskLevel = 'mid';
                                } else {
                                  riskLevel = 'low';
                                }

                                return {
                                  korean: ingredient.kor || '',
                                  english: ingredient.eng || '',
                                  risk: riskLevel,
                                  score: riskValue,
                                  desc: ingredient.desc || '성분 설명 없음',
                                };
                              });
                            };

                            const processedIngredients = processIngredients(unifiedProduct.ingredients);

                            const riskCounts = {
                              unknown: processedIngredients.filter((i) => i.risk === 'unknown').length,
                              low: processedIngredients.filter((i) => i.risk === 'low').length,
                              mid: processedIngredients.filter((i) => i.risk === 'mid').length,
                              high: processedIngredients.filter((i) => i.risk === 'high').length,
                            };

                            const mediumHighCount = riskCounts.mid + riskCounts.high;

                            return (
                              <>
                                {/* Safety Assessment */}
                                <div className="mb-3 p-2 bg-light rounded">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <small className="fw-bold">Safety Assessment</small>
                                    <span
                                      className={`small ${mediumHighCount > 3 ? 'text-danger' : mediumHighCount > 0 ? 'text-warning' : 'text-success'}`}
                                      style={{ fontSize: '0.7rem' }}
                                    >
                                      {mediumHighCount} medium-high risk
                                    </span>
                                  </div>

                                  <Row className="g-2">
                                    <Col xs={3}>
                                      <div className="text-center">
                                        <div className="fw-bold text-success" style={{ fontSize: '0.9rem' }}>
                                          {riskCounts.low}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                          Low
                                        </small>
                                      </div>
                                    </Col>
                                    <Col xs={3}>
                                      <div className="text-center">
                                        <div className="fw-bold text-warning" style={{ fontSize: '0.9rem' }}>
                                          {riskCounts.mid}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                          Mid
                                        </small>
                                      </div>
                                    </Col>
                                    <Col xs={3}>
                                      <div className="text-center">
                                        <div className="fw-bold text-danger" style={{ fontSize: '0.9rem' }}>
                                          {riskCounts.high}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                          High
                                        </small>
                                      </div>
                                    </Col>
                                    <Col xs={3}>
                                      <div className="text-center">
                                        <div className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>
                                          {riskCounts.unknown}
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.65rem' }}>
                                          Unknown
                                        </small>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>

                                {/* Ingredients List */}
                                <IngredientsGrid ingredients={processedIngredients} />
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Coupang Data - Horizontal Layout */}
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <img src="/assets/coupang-logo.png" alt="Coupang" style={{ height: '20px', width: 'auto' }} />
                  </div>
                  <div className="d-flex gap-3">
                    {/* Coupang Thumbnail */}
                    <div className="flex-shrink-0">
                      <img
                        src={
                          coupangProduct?.thumbnailImageUrl ||
                          coupangProduct?.heroImageUrls?.[0] ||
                          '/placeholder-image.png'
                        }
                        alt={coupangProduct?.name}
                        className="rounded border"
                        style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                      />
                    </div>
                    {/* Coupang Text */}
                    <div className="flex-grow-1">
                      <div className="mb-2">
                        <small className="text-muted fw-bold">Brand</small>
                        <div style={{ fontSize: '0.85rem' }}>
                          {coupangProduct?.brand || (
                            <Badge bg="warning" text="dark" style={{ fontSize: '0.65rem' }}>
                              3rd-Party Seller
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Product Name</small>
                        <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{coupangProduct?.name}</div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Price</small>
                        <div className="d-flex align-items-baseline">
                          <span className="text-danger fw-bold me-2" style={{ fontSize: '1rem' }}>
                            {unifiedProduct.priceInfo?.salePrice || 'N/A'}
                          </span>
                          {unifiedProduct.priceInfo?.originalPrice &&
                            unifiedProduct.priceInfo.originalPrice !== unifiedProduct.priceInfo.salePrice && (
                              <span className="text-muted text-decoration-line-through" style={{ fontSize: '0.75rem' }}>
                                {unifiedProduct.priceInfo.originalPrice}
                              </span>
                            )}
                        </div>
                        {coupangProduct?.outOfStock !== undefined && coupangProduct.outOfStock && (
                          <Badge bg="danger" className="mt-1" style={{ fontSize: '0.65rem' }}>
                            OUT OF STOCK
                          </Badge>
                        )}
                      </div>

                      {unifiedProduct.coupangRating && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Rating</small>
                          <div className="d-flex align-items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                size={12}
                                className={`${i < Math.floor(unifiedProduct.coupangRating || 0) ? 'text-warning' : 'text-muted'}`}
                                fill="currentColor"
                              />
                            ))}
                            <span className="ms-2" style={{ fontSize: '0.85rem' }}>
                              {unifiedProduct.coupangRating}
                            </span>
                            {unifiedProduct.coupangReviews && (
                              <small className="text-muted ms-1">
                                ({unifiedProduct.coupangReviews.toLocaleString()})
                              </small>
                            )}
                          </div>
                        </div>
                      )}

                      {unifiedProduct.sellerInfo?.sellerName && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Seller</small>
                          <div style={{ fontSize: '0.85rem' }}>{unifiedProduct.sellerInfo.sellerName}</div>
                          {unifiedProduct.sellerInfo.sellerRating && (
                            <small className="text-muted">★ {unifiedProduct.sellerInfo.sellerRating}</small>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Col>

            {/* Right Column - Product Info Validation & Translation */}
            <Col md={6} style={{ position: 'relative' }}>
              {/* Loading Overlay */}
              {isLoadingTranslation && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    backdropFilter: 'blur(2px)',
                  }}
                >
                  <Spinner animation="border" variant="success" />
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.9rem' }}>
                    Validating & translating product info...
                  </p>
                </div>
              )}
              <div className="p-4">
                <div className="mb-4">
                  <div className="text-center mb-3">
                    <h6 className="mb-2 fw-bold text-success">Product Info Validation & Translation</h6>
                    <hr className="my-2" />
                  </div>

                  {/* Action Buttons at Top */}
                  <div className="d-flex gap-2 justify-content-center mb-3 pb-3 border-bottom">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={fetchTranslation}
                      disabled={isLoadingTranslation}
                    >
                      {isLoadingTranslation ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-1" />
                          Validating & Translating...
                        </>
                      ) : (
                        <>
                          <Download size={14} className="me-1" />
                          {translationData || glowpickProduct?.scAgentEngData
                            ? 'Refresh Validation & Translation'
                            : 'Validate & Translate'}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleEditClick}
                      disabled={!translationData && !glowpickProduct?.scAgentEngData}
                    >
                      <Edit2 size={14} className="me-1" />
                      Edit Product Info
                    </Button>
                    {translationData && (
                      <Button variant="success" size="sm" onClick={handleSaveToGlowpick} disabled={isSavingTranslation}>
                        {isSavingTranslation ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-1" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={14} className="me-1" />
                            Save Product Info
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {(() => {
                  const scAgentEngData = translationData || glowpickProduct?.scAgentEngData;

                  if (!scAgentEngData) {
                    return (
                      <div className="text-center py-5 text-muted">
                        <div className="mb-2">No product information in English available</div>
                        <small>Click &quot;Fetch Translation&quot; button above to get started</small>
                      </div>
                    );
                  }

                  return (
                    <>
                      {/* Screening Flag Warning */}
                      {scAgentEngData.screeningResult && scAgentEngData.screeningResult.flag === true && (
                        <Alert variant="warning" className="mb-3 d-flex align-items-start">
                          <AlertTriangle size={20} className="me-2 mt-1 flex-shrink-0" />
                          <div className="flex-grow-1">
                            <div className="fw-bold mb-1">⚠️ Product Flagged</div>
                            <div style={{ fontSize: '0.85rem' }}>
                              {scAgentEngData.screeningResult.flagReason || 'This product has been flagged.'}
                            </div>
                            {scAgentEngData.screeningResult.confidence !== undefined && (
                              <div className="mt-1">
                                <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>
                                  Confidence: {Math.round(scAgentEngData.screeningResult.confidence * 100)}%
                                </Badge>
                              </div>
                            )}
                          </div>
                        </Alert>
                      )}

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Brand</small>
                        <div style={{ fontSize: '0.85rem' }}>{scAgentEngData.brand || 'N/A'}</div>
                        {scAgentEngData.brandSlug && (
                          <small className="text-muted">Slug: {scAgentEngData.brandSlug}</small>
                        )}
                      </div>

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Product Name</small>
                        <div style={{ fontSize: '0.85rem', fontWeight: '500' }}>{scAgentEngData.name || 'N/A'}</div>
                      </div>

                      <div className="mb-2">
                        <small className="text-muted fw-bold">Categories</small>
                        <div style={{ fontSize: '0.85rem' }}>{scAgentEngData.categories || 'N/A'}</div>
                        {scAgentEngData.leafCategorySlug && (
                          <small className="text-muted">Slug: {scAgentEngData.leafCategorySlug}</small>
                        )}
                      </div>

                      {scAgentEngData.description && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Description</small>
                          <div style={{ fontSize: '0.85rem', lineHeight: '1.5' }}>{scAgentEngData.description}</div>
                        </div>
                      )}

                      {scAgentEngData.keyBenefits && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Key Benefits</small>
                          <div style={{ fontSize: '0.85rem', lineHeight: '1.5', whiteSpace: 'pre-line' }}>
                            {scAgentEngData.keyBenefits}
                          </div>
                        </div>
                      )}

                      {scAgentEngData.keyIngredients && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Key Ingredients</small>
                          <div style={{ fontSize: '0.85rem' }}>{scAgentEngData.keyIngredients}</div>
                        </div>
                      )}

                      {scAgentEngData.efficacyTags && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Efficacy Tags</small>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {scAgentEngData.efficacyTags
                              .split(',')
                              .map((t: string) => t.trim())
                              .join(' • ')}
                          </div>
                        </div>
                      )}

                      {scAgentEngData.skinConcernTags && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Skin Concern Tags</small>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {scAgentEngData.skinConcernTags
                              .split(',')
                              .map((t: string) => t.trim())
                              .join(' • ')}
                          </div>
                        </div>
                      )}

                      {scAgentEngData.skinTypeTags && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Skin Type Tags</small>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {scAgentEngData.skinTypeTags
                              .split(',')
                              .map((t: string) => t.trim())
                              .join(' • ')}
                          </div>
                        </div>
                      )}

                      {scAgentEngData.tags && (
                        <div className="mb-2">
                          <small className="text-muted fw-bold">Tags</small>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {scAgentEngData.tags
                              .split(',')
                              .map((t: string) => t.trim())
                              .join(' • ')}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Edit Product Info Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Product Info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editedData && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Brand</Form.Label>
                    <Form.Control
                      type="text"
                      value={editedData.brand || ''}
                      onChange={(e) => setEditedData({ ...editedData, brand: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Brand Slug</Form.Label>
                    <Form.Control
                      type="text"
                      value={editedData.brandSlug || ''}
                      onChange={(e) => setEditedData({ ...editedData, brandSlug: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Product Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editedData.name || ''}
                  onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categories</Form.Label>
                    <Form.Control
                      type="text"
                      value={editedData.categories || ''}
                      onChange={(e) => setEditedData({ ...editedData, categories: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Category Slug</Form.Label>
                    <Form.Control
                      type="text"
                      value={editedData.leafCategorySlug || ''}
                      onChange={(e) => setEditedData({ ...editedData, leafCategorySlug: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editedData.description || ''}
                  onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Key Benefits</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editedData.keyBenefits || ''}
                  onChange={(e) => setEditedData({ ...editedData, keyBenefits: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Key Ingredients</Form.Label>
                <Form.Control
                  type="text"
                  value={editedData.keyIngredients || ''}
                  onChange={(e) => setEditedData({ ...editedData, keyIngredients: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Efficacy Tags (comma-separated)</Form.Label>
                <Form.Control
                  type="text"
                  value={editedData.efficacyTags || ''}
                  onChange={(e) => setEditedData({ ...editedData, efficacyTags: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Skin Concern Tags (comma-separated)</Form.Label>
                <Form.Control
                  type="text"
                  value={editedData.skinConcernTags || ''}
                  onChange={(e) => setEditedData({ ...editedData, skinConcernTags: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Skin Type Tags (comma-separated)</Form.Label>
                <Form.Control
                  type="text"
                  value={editedData.skinTypeTags || ''}
                  onChange={(e) => setEditedData({ ...editedData, skinTypeTags: e.target.value })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Tags (comma-separated)</Form.Label>
                <Form.Control
                  type="text"
                  value={editedData.tags || ''}
                  onChange={(e) => setEditedData({ ...editedData, tags: e.target.value })}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSaveToGlowpick} disabled={isSavingTranslation}>
            {isSavingTranslation ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving...
              </>
            ) : (
              <>
                <Save size={14} className="me-1" />
                Save Product Info
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notifications */}
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '80px' }}>
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={toastVariant}
          style={{ minWidth: '400px', fontSize: '1rem' }}
        >
          <Toast.Header closeButton={false}>
            <strong className="me-auto" style={{ fontSize: '1.1rem' }}>
              {toastVariant === 'success' ? '✅ Success' : '❌ Error'}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white" style={{ fontSize: '0.95rem' }}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};
